import { useState } from 'react';

import { useApolloClient } from '@apollo/client';
import { getOperationName } from '@apollo/client/utilities';
import { useGraphQLMutation } from '@betterment/js-runtime';
import { Banner, Box, Button, Dialog, Heading, Text } from '@betterment/samba-react';
import * as Sentry from '@sentry/browser';

import { trackClick } from '../../../../features/track';
import { CancelTransactionRequestDocument } from '../../../graphql/mutation.cancelTransactionRequest.generated';
import { ActivityItemsDocument } from '../../../graphql/query.activityItems.generated';

interface CancelButtonProps {
  transactionType?: string;
  transactionId: string;
  to: string;
  amount: string;
}

export const CancelButton: React.FC<CancelButtonProps> = ({ transactionType, transactionId, to, amount }) => {
  const client = useApolloClient();

  const [cancelTransaction, { loading, error }] = useGraphQLMutation(CancelTransactionRequestDocument, {
    onCompleted: () =>
      client.refetchQueries({
        include: [getOperationName(ActivityItemsDocument) as string],
      }),
  });
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCancel = async () => {
    try {
      const mutationResult = await cancelTransaction({ variables: { id: transactionId } });
      if (!mutationResult.errors) setIsModalOpen(false);
    } catch (e) {
      Sentry.captureException(e);
    }
  };

  return (
    <Dialog open={isModalOpen} onOpenChange={setIsModalOpen} overlay="default">
      <Dialog.Trigger asChild={true}>
        <Box css={{ py: 'space.200' }} onClick={() => trackClick({ name: `ActivityCancel${transactionType}` })}>
          <Button variant="textCritical" disabled={loading}>
            <Text variant="text100">Cancel {transactionType}</Text>
          </Button>
        </Box>
      </Dialog.Trigger>
      <Dialog.Content size="large" css={{ display: 'flex', flexDirection: 'column', p: 'space.400', gap: 'space.400' }}>
        <Box css={{ display: 'flex', flexDirection: 'column', gap: 'space.150' }}>
          <Dialog.Title asChild={true}>
            <Heading variant="title300">Are you sure you want to cancel?</Heading>
          </Dialog.Title>
          <Dialog.Description asChild={true}>
            <Text color="text" variant="text100">
              Do you want to cancel your {amount} {transactionType} to {to}?
            </Text>
          </Dialog.Description>
        </Box>
        {error && (
          <Banner variant="critical">
            <Banner.Content>
              <Text as="p">Something went wrong.</Text>
            </Banner.Content>
          </Banner>
        )}
        <Box css={{ display: 'flex', gap: 'space.200' }}>
          <Button onClick={handleCancel} variant="solidCritical" disabled={loading}>
            <Text variant="text100">Cancel {transactionType}</Text>
          </Button>

          <Dialog.Close asChild={true}>
            <Button variant="solidSecondary">Nevermind</Button>
          </Dialog.Close>
        </Box>
      </Dialog.Content>
    </Dialog>
  );
};
